import { defineStore } from 'pinia'
import { $fetch } from 'ofetch'
import dayjs from 'dayjs'
import type { Ref } from 'vue'
import type { Check } from '~/types/checks'

/**
 * Single source of truth for checks across the application.
 * Owns the ONE realtime subscription for the checks table.
 * Used by: POS (checksStore, activeCheck, posCheck), KDS (via kdsChecks)
 */
export const useChecksSyncStore = defineStore('checksSync', () => {
  const log = useLogger('store:checksSync', true)
  const { get } = useAuthenticatedFetch()
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)

  // POS checks (open only)
  const checks: Ref<Check[]> = ref([])
  const loading: Ref<boolean> = ref(false)

  // KDS checks (today's open + closed)
  const kdsChecks: Ref<Check[]> = ref([])
  const kdsLoading: Ref<boolean> = ref(false)

  const supabase = useSupabaseClient()
  const realtimeChannel = ref()

  // ─── Fetch ────────────────────────────────────────────────────────────────

  async function fetchChecks(status?: string): Promise<void> {
    if (!businessId.value) {
      log.warn('fetch', 'Cannot fetch checks: businessId not available')
      return
    }

    loading.value = true
    try {
      const queryParam = status === 'all' ? '?all=true' : status ? `?status=${status}` : '?status=open'
      const res = await get(`/api/business/${businessId.value}/checks${queryParam}`)
      checks.value = res.data || []
      log.info('fetch', `Fetched ${checks.value.length} checks`)
    }
    catch (error: any) {
      if (error.statusCode === 403 || error.response?.status === 403) {
        log.debug('fetch:skip', 'Checks not available for this business (counter mode or no permission)')
        checks.value = []
        return
      }
      log.error('fetch:error', error)
      throw error
    }
    finally {
      loading.value = false
    }
  }

  async function fetchKdsChecks(): Promise<void> {
    if (!businessId.value) return
    kdsLoading.value = true
    try {
      const startOfToday = dayjs().startOf('day').toISOString()
      const data = await $fetch(`/api/business/${businessId.value}/kds/checks`, {
        params: { status: 'open,closed', start: startOfToday },
      })
      kdsChecks.value = (data as Check[]) || []
      log.info('fetchKds', `Fetched ${kdsChecks.value.length} KDS checks`)
    }
    catch (error: any) {
      log.error('fetchKds:error', error)
    }
    finally {
      kdsLoading.value = false
    }
  }

  // ─── Array helpers ────────────────────────────────────────────────────────

  function updateCheckInArray(updatedCheck: Check): void {
    const checkIndex = checks.value.findIndex(c => c.id === updatedCheck.id)
    if (checkIndex !== -1) {
      checks.value.splice(checkIndex, 1, updatedCheck)
    }
    else {
      checks.value.push(updatedCheck)
    }
  }

  function removeCheckFromArray(checkId: number): void {
    const checkIndex = checks.value.findIndex(c => c.id === checkId)
    if (checkIndex !== -1) {
      checks.value.splice(checkIndex, 1)
      log.info('remove', `Removed check #${checkId} from array`)
    }
  }

  function getCheckById(checkId: number): Check | undefined {
    return checks.value.find(c => c.id === checkId)
  }

  // ─── Realtime event handlers ──────────────────────────────────────────────

  function handleCheckUpdate(payload: any): void {
    const incoming = payload.new
    const checkId = incoming?.id
    if (!checkId) return

    log.info('realtime:update', `check #${checkId}`)

    // Always sync kdsChecks — KDS shows open AND closed checks
    const kdsIdx = kdsChecks.value.findIndex(c => c.id === checkId)
    if (kdsIdx !== -1) {
      const existing = kdsChecks.value[kdsIdx]
      kdsChecks.value.splice(kdsIdx, 1, { ...existing, ...incoming })
      log.info('realtime:kds-sync', `Updated KDS check #${checkId}`)
    }

    const posCheckStore = usePosCheckStore()

    // Remove soft-deleted checks immediately
    if (incoming.deleted_at) {
      removeCheckFromArray(checkId)
      const kdsDelIdx = kdsChecks.value.findIndex(c => c.id === checkId)
      if (kdsDelIdx !== -1) kdsChecks.value.splice(kdsDelIdx, 1)
      log.info('realtime:removed', `Removed soft-deleted check #${checkId}`)
      return
    }

    // Remove from POS list if check closed/cancelled
    if (incoming.status === 'closed' || incoming.status === 'cancelled') {
      removeCheckFromArray(checkId)
      if (posCheckStore.activeCheckId === checkId && !posCheckStore.isEditingClosedCheck) {
        posCheckStore.clearActiveCheck()
        useSuccessToast('Check closed successfully')
      }
      return
    }

    // Active POS check: merge only kitchen fields — preserve waiter edits
    if (posCheckStore.activeCheckId === checkId) {
      if (incoming.cart?.length) {
        posCheckStore.mergeKitchenStatusesFromPayload(incoming.cart)
        log.info('realtime:kitchen-sync', `Merged KDS statuses into active check #${checkId}`)
      }
      return
    }

    // Non-active check: merge partial payload into POS list
    const existingCheck = checks.value.find(c => c.id === checkId)
    if (existingCheck) {
      updateCheckInArray({ ...existingCheck, ...incoming })
    }
  }

  function handleCheckInsert(payload: any): void {
    const newCheck = payload.new as Check
    if (newCheck.business_id && newCheck.business_id !== businessId.value) return
    log.info('realtime:insert', 'New check created:', newCheck.id)
    updateCheckInArray(newCheck)
    if (!kdsChecks.value.some(c => c.id === newCheck.id)) {
      kdsChecks.value.push(newCheck)
    }
  }

  function handleCheckDelete(payload: any): void {
    const deletedCheck = payload.old as Check
    const inChecks = checks.value.some(c => c.id === deletedCheck.id)
    const inKdsChecks = kdsChecks.value.some(c => c.id === deletedCheck.id)
    if (!inChecks && !inKdsChecks) return
    log.info('realtime:delete', 'Check deleted:', deletedCheck.id)
    removeCheckFromArray(deletedCheck.id)
    const kdsIdx = kdsChecks.value.findIndex(c => c.id === deletedCheck.id)
    if (kdsIdx !== -1) kdsChecks.value.splice(kdsIdx, 1)
  }

  // ─── Realtime subscription ────────────────────────────────────────────────

  function setupRealtime(): void {
    if (!businessId.value) {
      log.warn('realtime:setup', 'Cannot setup realtime: businessId not available')
      return
    }

    if (realtimeChannel.value) {
      log.warn('realtime:setup', 'Realtime already setup, skipping')
      return
    }

    log.info('realtime:setup', 'Setting up checks realtime for business:', businessId.value)

    realtimeChannel.value = supabase
      .channel(`checks-sync-${businessId.value}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'checks' }, handleCheckUpdate)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'checks' }, handleCheckInsert)
      .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'checks' }, handleCheckDelete)
      .subscribe((status, error) => {
        log.info('realtime:status', status)
        if (error) log.error('realtime:error', error)

        if (status === 'SUBSCRIBED') {
          log.info('realtime:subscribed', '✅ Subscribed to checks updates')
          // Re-fetch KDS data on reconnect to catch any missed events
          if (kdsChecks.value.length > 0) {
            fetchKdsChecks()
          }
        }
        if (status === 'CHANNEL_ERROR') {
          log.debug('realtime:channel-error', 'Realtime channel error — manual refresh may be needed')
          cleanupRealtime()
        }
        if (status === 'TIMED_OUT') {
          log.error('realtime:timeout', 'Subscription timed out')
        }
        if (status === 'CLOSED') {
          log.warn('realtime:closed', 'Channel closed')
        }
      })
  }

  function cleanupRealtime(): void {
    if (realtimeChannel.value) {
      supabase.removeChannel(realtimeChannel.value)
      realtimeChannel.value = null
      log.info('realtime:cleanup', 'Realtime channel cleaned up')
    }
  }

  // Watch businessId to reset state and trigger initial fetch
  watch(businessId, async (newId, oldId) => {
    if (oldId) {
      cleanupRealtime()
      checks.value = []
      kdsChecks.value = []
    }
    if (newId) {
      await fetchChecks()
    }
  }, { immediate: true })

  // Gate realtime on KDS being enabled — wait for businessInfo before subscribing
  watch(() => businessStore.businessInfo, (newInfo) => {
    if (!businessId.value || !newInfo) return
    const kdsEnabled = newInfo?.pos_config?.enable_kds !== false
    if (kdsEnabled && !realtimeChannel.value) {
      setupRealtime()
    }
    else if (!kdsEnabled) {
      cleanupRealtime()
    }
  }, { immediate: true })

  function reset(): void {
    cleanupRealtime()
    checks.value = []
    kdsChecks.value = []
    loading.value = false
    kdsLoading.value = false
  }

  return {
    // State
    checks,
    loading,
    kdsChecks,
    kdsLoading,

    // Methods
    fetchChecks,
    fetchKdsChecks,
    updateCheckInArray,
    removeCheckFromArray,
    getCheckById,
    setupRealtime,
    cleanupRealtime,
    reset,
  }
}, {
  persist: {
    key: 'checksSync',
    storage: localStorage,
    paths: ['checks'],
  },
})
